import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Users, CheckCircle, AlertTriangle, XCircle, Lock, Unlock, Trash2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKING_TYPE_LABELS = {
  bq_abo: "BQ-Abonnement",
  hq_abo: "HQ-Abonnement",
  bq_intensiv: "BQ-Intensivkurs",
  hq_intensiv: "HQ-Intensivkurs",
  probetraining: "Probetraining",
  mathe_grundkurs: "Mathe-Grundkurs",
  fachgespraech: "Fachgespräch"
};

const HQ_SUB_LABELS = {
  metall: "HQ Abo Metalltechnik"
};

const StatusBadge = ({ status }) => {
  const config = {
    active: { class: "status-active", label: "Aktiv", icon: CheckCircle },
    expiring_soon: { class: "status-warning", label: "Läuft bald ab", icon: AlertTriangle },
    expired: { class: "status-expired", label: "Abgelaufen", icon: XCircle },
    scheduled: { class: "bg-blue-500/15 text-blue-500 border-blue-500/50 border", label: "Geplant", icon: null }
  };
  
  const { class: className, label } = config[status] || config.expired;
  
  return (
    <Badge className={`${className} rounded-sm text-xs font-medium`}>
      {label}
    </Badge>
  );
};

const BookingPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingBooking, setDeletingBooking] = useState(null);

  useEffect(() => {
    fetchData();
  }, [slug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get page info
      const pagesRes = await axios.get(`${API}/pages`);
      const foundPage = pagesRes.data.find(p => p.slug === slug);
      setPage(foundPage);

      if (foundPage) {
        // Get bookings for this page type
        let url = `${API}/bookings/type/${foundPage.booking_type}`;
        if (foundPage.hq_sub_type) {
          url += `?hq_sub_type=${foundPage.hq_sub_type}`;
        }
        const bookingsRes = await axios.get(url);
        setBookings(bookingsRes.data);
      }
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLock = async (userId, isLocked) => {
    if (!userId) {
      toast.error("Kein Benutzer zugeordnet");
      return;
    }
    try {
      await axios.put(`${API}/users/${userId}`, { is_locked: !isLocked });
      toast.success(isLocked ? "Teilnehmer entsperrt" : "Teilnehmer gesperrt");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Ändern");
    }
  };

  const handleDeleteBooking = async () => {
    if (!deletingBooking) return;
    try {
      await axios.delete(`${API}/bookings/${deletingBooking.id}`);
      toast.success("Buchung gelöscht");
      setDeleteDialogOpen(false);
      setDeletingBooking(null);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const openDeleteDialog = (booking) => {
    setDeletingBooking(booking);
    setDeleteDialogOpen(true);
  };

  const title = page?.name || "Buchungen";
  const isAbo = page?.booking_type?.includes("_abo");
  const isProbe = page?.booking_type === "probetraining";

  return (
    <AdminLayout title={title}>
      <div className="space-y-6" data-testid={`booking-page-${slug}`}>
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Gesamt</p>
                  <p className="text-2xl font-bold text-primary">{bookings.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Aktiv</p>
                  <p className="text-2xl font-bold text-green-500">
                    {bookings.filter(b => b.status === "active").length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {isAbo && (
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">Ablaufend</p>
                    <p className="text-2xl font-bold text-yellow-500">
                      {bookings.filter(b => b.status === "expiring_soon").length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Table */}
        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle>Teilnehmer mit {title}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : bookings.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Keine Teilnehmer mit dieser Buchung</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Name</TableHead>
                    <TableHead className="text-muted-foreground">E-Mail</TableHead>
                    <TableHead className="text-muted-foreground">Fachrichtung</TableHead>
                    {isAbo && <TableHead className="text-muted-foreground">Start</TableHead>}
                    {isAbo && <TableHead className="text-muted-foreground">Ende</TableHead>}
                    {isAbo && <TableHead className="text-muted-foreground">Restlaufzeit</TableHead>}
                    {isProbe && <TableHead className="text-muted-foreground">Fach</TableHead>}
                    {isProbe && <TableHead className="text-muted-foreground">Datum</TableHead>}
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((booking) => (
                    <TableRow key={booking.id} className="border-border">
                      <TableCell className="font-medium">
                        {booking.user?.name || <span className="text-muted-foreground italic">Kein Benutzer</span>}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-sm">
                        {booking.user?.email || "-"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {booking.user?.industriemeister_fachrichtung || "-"}
                      </TableCell>
                      {isAbo && <TableCell className="font-mono text-sm">{booking.start_date}</TableCell>}
                      {isAbo && (
                        <TableCell className="font-mono text-sm">
                          {booking.is_unlimited ? <span className="text-green-500">∞</span> : booking.end_date}
                        </TableCell>
                      )}
                      {isAbo && (
                        <TableCell>
                          {booking.days_remaining !== null ? (
                            <span className={`font-mono ${
                              booking.status === "expiring_soon" ? "text-yellow-500" : 
                              booking.status === "expired" ? "text-red-500" : ""
                            }`}>
                              {booking.days_remaining} Tage
                            </span>
                          ) : (
                            <span className="text-green-500">∞</span>
                          )}
                        </TableCell>
                      )}
                      {isProbe && <TableCell>{booking.probetraining_subject}</TableCell>}
                      {isProbe && <TableCell className="font-mono text-sm">{booking.probetraining_date}</TableCell>}
                      <TableCell>
                        <StatusBadge status={booking.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {booking.user?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleLock(booking.user?.id, booking.user?.is_locked)}
                              title={booking.user?.is_locked ? "Entsperren" : "Sperren"}
                            >
                              {booking.user?.is_locked ? (
                                <Unlock className="h-4 w-4 text-green-500" />
                              ) : (
                                <Lock className="h-4 w-4 text-yellow-500" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(booking)}
                            title="Buchung löschen"
                            data-testid={`delete-booking-${booking.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Buchung löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                {deletingBooking?.user?.name ? (
                  <>Möchten Sie die Buchung von &ldquo;{deletingBooking.user.name}&rdquo; wirklich löschen?</>
                ) : (
                  <>Möchten Sie diese verwaiste Buchung wirklich löschen?</>
                )}
                <br /><br />
                Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteBooking}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default BookingPage;
